import { Logger, Utils, StringUtils, ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../../ComponentTypes";
import { Tweens, TweensAttributes } from "../../Effects/Tweens";
import { SwitchStyle } from "../Styles/SwitchStyle";
import { StyleManager } from "../../Managers/StyleManager";
import { IElement } from "../Interfaces/IElement";
import { Attributes } from "../../../Utils/Attributes";
import { BaseElementAttributes } from "./BaseElement";
import { CustomUtils } from "../../../Utils/CustomUtils";

/**
 * ShapeElement allows you to display vector art in the display list.
 * Example:
 *   <ShapeElement Class="ShapeElement" X="0" Y="0" Width="100" Height="100" Color="FF0000" Mask="ShapeElement" />
 *
 * @class ShapeElement
 * @extends PIXI.Graphics
 * @implements IElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ShapeElement extends PIXI.Graphics implements IElement {
    private _uid: string;
    protected _width: number;
    protected _height: number;
    protected _x: number = 0;
    protected _y: number = 0;
    public _lineWidth: number;
    public _lineColor: number;
    public _lineAlpha: number;
    public _fillAlpha: number;
    public _color: number = 0xff0000;

    protected style: SwitchStyle;
    public _tweenEngine: string;

    constructor(opts?: IShapeElementAttributes) {
        super();

        this._uid = `${this.componentName}_${Utils.generateUuid()}`;

        if (opts) {
            this.adjust(opts);
        }
    }

    get tweenEngine(): string {
        return this._tweenEngine;
    }

    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        Tweens.removeTweens(this);
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element): void {
        Tweens.removeTweens(this);

        if (this.name == null) {
            this.name = node.nodeName;
        }

        if (node.hasAttribute("Style")) {
            this.style = <SwitchStyle>StyleManager.Styles[node.getAttribute(ShapeElementAttributes.STYLE)];
            if (this.style != null) {
                //console.log('test shapeElement style');
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name: " + node.getAttribute(ShapeElementAttributes.STYLE));
                }
            }
        }

        if (!this.updateByNode(node)) {
            this.redraw();
        }
    }

    public redraw(): void {
        this.clear();
        this.beginFill(this._color, this._fillAlpha);
        this.lineStyle(this._lineWidth, this._lineColor, this._lineAlpha);
        this.drawRect(this._x, this._y, this._width, this._height);
        this.endFill();
    }

    /**
     * @todo Najst miesta, kde sa vola metoda create(). Ak by bolo vhodne, aby sa tam volala metoda update(), treba volat ju. Tomu treba prisposobit aj obsah metody.
     *
     * @param node
     */
    public updateByNode(node: Element): boolean {
        let redraw: boolean = false;

        if (node.hasAttribute(ShapeElementAttributes.LINE_COLOR)) {
            this._lineColor = parseInt(node.getAttribute(ShapeElementAttributes.LINE_COLOR), 16);
            redraw = true;
        }
        if (node.hasAttribute(ShapeElementAttributes.LINE_WIDTH)) {
            this._lineWidth = parseInt(node.getAttribute(ShapeElementAttributes.LINE_WIDTH));
            redraw = true;
        }
        if (node.hasAttribute(ShapeElementAttributes.LINE_ALPHA)) {
            this._lineAlpha = parseInt(node.getAttribute(ShapeElementAttributes.LINE_ALPHA));
            redraw = true;
        }

        if (node.hasAttribute(ShapeElementAttributes.COLOR)) {
            this._color = parseInt(node.getAttribute(ShapeElementAttributes.COLOR), 16);
            redraw = true;
        }
        if (node.hasAttribute(ShapeElementAttributes.FILL_ALPHA)) {
            this._fillAlpha = parseInt(node.getAttribute(ShapeElementAttributes.FILL_ALPHA));
            redraw = true;
        }

        if (node.hasAttribute(ShapeElementAttributes.BLEND_MODE)) {
            this.blendMode = CustomUtils.getBlendMode(parseInt(node.getAttribute(ShapeElementAttributes.BLEND_MODE)));
        }

        if (node.hasAttribute(ShapeElementAttributes.VISIBLE)) {
            this.visible = StringUtils.parseBoolean(node.getAttribute(ShapeElementAttributes.VISIBLE));
        }

        if (node.hasAttribute(ShapeElementAttributes.POSITION_X)) {
            this.position.x = parseInt(node.getAttribute(ShapeElementAttributes.POSITION_X));
        }

        if (node.hasAttribute(ShapeElementAttributes.POSITION_Y)) {
            this.position.y = parseInt(node.getAttribute(ShapeElementAttributes.POSITION_Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.X)) {
            this._x = parseInt(node.getAttribute(ShapeElementAttributes.X));
            redraw = true;
        }

        if (node.hasAttribute(ShapeElementAttributes.Y)) {
            this._y = parseInt(node.getAttribute(ShapeElementAttributes.Y));
            redraw = true;
        }

        if (node.hasAttribute(ShapeElementAttributes.WIDTH)) {
            this._width = parseInt(node.getAttribute(ShapeElementAttributes.WIDTH));
            redraw = true;
        }

        if (node.hasAttribute(ShapeElementAttributes.HEIGHT)) {
            this._height = parseInt(node.getAttribute(ShapeElementAttributes.HEIGHT));
            redraw = true;
        }

        if (node.hasAttribute(ShapeElementAttributes.ALPHA)) {
            this.alpha = parseFloat(node.getAttribute(ShapeElementAttributes.ALPHA));
        }

        if (node.hasAttribute(ShapeElementAttributes.PIVOT_X)) {
            this.pivot.x = parseFloat(node.getAttribute(ShapeElementAttributes.PIVOT_X));
        }
        if (node.hasAttribute(ShapeElementAttributes.PIVOT_Y)) {
            this.pivot.y = parseFloat(node.getAttribute(ShapeElementAttributes.PIVOT_Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE));
            this.scale.y = this.scale.x;
        }
        if (node.hasAttribute(ShapeElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE_X));
        }
        if (node.hasAttribute(ShapeElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(ShapeElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(ShapeElementAttributes.MASK)) {
                    let shapeElement: ShapeElement = new ShapeElement();
                    shapeElement.create(next);
                    this.mask = shapeElement;
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(ShapeElementAttributes.MASK));
                }
            }
        }

        this.runAlphaTween(node);
        this.runPositionTween(node);
        this.runRotationTween(node);
        this.runScaleTween(node);
        this.runSkewTween(node);
        this.runTween(node);

        if (redraw) {
            this.redraw();
        }
        return redraw;
    }

    public adjust(opts: IShapeElementAttributes) {
        opts = opts || {};
        let realign = false;
        if (opts.x !== undefined && opts.x !== null) {
            this._x = opts.x;
            realign = true;
        }
        if (opts.y !== undefined && opts.y !== null) {
            this._y = opts.y;
            realign = true;
        }
        if (opts.width !== undefined && opts.width !== null) {
            this._width = opts.width;
            realign = true;
        }
        if (opts.height !== undefined && opts.height !== null) {
            this._height = opts.height;
            realign = true;
        }

        if (opts.color !== undefined && opts.color !== null) {
            this._color = opts.color;
            realign = true;
        }
        if (opts.lineColor !== undefined && opts.lineColor !== null) {
            this._lineColor = opts.lineColor;
            realign = true;
        }
        if (opts.lineWidth !== undefined && opts.lineWidth !== null) {
            this._lineWidth = opts.lineWidth;
            realign = true;
        }
        if (opts.alpha !== undefined && opts.alpha !== null) {
            this.alpha = opts.alpha;
            realign = true;
        }

        if (realign) {
            this.redraw();
        }
    }

    /**
     * Runs the alpha tween
     * @method runAlphaTween
     * @param node
     * @returns {boolean}
     */
    public runAlphaTween(node: Element): boolean {
        if (!node.hasAttribute(BaseElementAttributes.ALPHA_TWEEN)) {
            return false;
        }

        Tweens.removeTweens(this, "alpha");

        let alphaTween: Array<number>;

        let alphaTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.ALPHA_TWEEN_POSITION,
            null,
        );

        if (alphaTweenPosition == BaseElementAttributes.ALPHA_TWEEN_POSITION_VALUE.RELATIVE) {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ALPHA_TWEEN)).map(
                (value) => value + this.alpha,
            );
        } else if (alphaTweenPosition == BaseElementAttributes.ALPHA_TWEEN_POSITION_VALUE.SMOOTH) {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ALPHA_TWEEN));
            if (alphaTween.length) {
                alphaTween[0] = this.alpha;
            }
        } else {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ALPHA_TWEEN));
        }

        const alphas: Array<number> = alphaTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(node, BaseElementAttributes.ALPHA_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.ALPHA_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(BaseElementAttributes.ALPHA_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(BaseElementAttributes.ALPHA_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.ALPHA_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(BaseElementAttributes.ALPHA_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ALPHA_TWEEN_KEY_TIMES))
            : null;

        Tweens.alphaTween(this, { alphas, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the position tween
     * @method runPositionTween
     * @param node
     * @returns {boolean}
     */
    public runPositionTween(node: Element): boolean {
        if (
            !node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_X) ||
            !node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_Y)
        ) {
            return false;
        }

        Tweens.removeTweens(this, "position");

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        let positionTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_POSITION,
            null,
        );
        if (positionTweenPosition == BaseElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_X)).map(
                (value) => value + this.position.x,
            );
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_Y)).map(
                (value) => value + this.position.y,
            );
        } else if (positionTweenPosition == BaseElementAttributes.POSITION_TWEEN_POSITION_VALUE.SMOOTH) {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_X));
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_Y));
            if (positionTweenX.length) {
                positionTweenX[0] = this.position.x;
            }
            if (positionTweenY.length) {
                positionTweenY[0] = this.position.y;
            }
        } else {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_X));
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_Y));
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(BaseElementAttributes.POSITION_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_KEY_TIMES))
            : null;

        Tweens.positionTween(this, { tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    public runSkewTween(node: Element): boolean {
        if (
            !node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_X) &&
            !node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_Y)
        ) {
            return false;
        }

        Tweens.removeTweens(this, "skew");

        let skewTweenX: Array<number>;
        let skewTweenY: Array<number>;

        let skewTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.SKEW_TWEEN_POSITION,
            null,
        );
        if (skewTweenPosition == BaseElementAttributes.SKEW_TWEEN_POSITION_VALUE.RELATIVE) {
            skewTweenX = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_X)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_X)).map(
                      (value) => Tweens.degrees2radians(value) + this.skew.x,
                  )
                : null;
            skewTweenY = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_Y)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_Y)).map(
                      (value) => Tweens.degrees2radians(value) + this.skew.y,
                  )
                : null;
        } else if (skewTweenPosition == BaseElementAttributes.SKEW_TWEEN_POSITION_VALUE.SMOOTH) {
            skewTweenX = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_X)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_X)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
            skewTweenY = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_Y)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_Y)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
            if (skewTweenX && skewTweenX.length) {
                skewTweenX[0] = this.skew.x;
            }
            if (skewTweenY && skewTweenY.length) {
                skewTweenY[0] = this.skew.y;
            }
        } else {
            skewTweenX = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_X)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_X)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
            skewTweenY = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_Y)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_Y)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
        }

        const tweenX: Array<number> = skewTweenX || [];
        const tweenY: Array<number> = skewTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(node, BaseElementAttributes.SKEW_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.SKEW_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(BaseElementAttributes.SKEW_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(node, BaseElementAttributes.SKEW_TWEEN_LOOP, null);
        const keyTimes: Array<number> = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_KEY_TIMES))
            : null;

        Tweens.skewTween(this, { tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the rotation tween
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runRotationTween(node: Element): boolean {
        if (!node.hasAttribute(BaseElementAttributes.ROTATION_TWEEN)) {
            return false;
        }

        Tweens.removeTweens(this, "rotation");

        let rotationTween: Array<number>;

        let rotationTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_POSITION,
            null,
        );
        if (rotationTweenPosition == BaseElementAttributes.ROTATION_TWEEN_POSITION_VALUE.RELATIVE) {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ROTATION_TWEEN)).map(
                (value) => value + Tweens.radians2degrees(this.rotation),
            );
        } else if (rotationTweenPosition == BaseElementAttributes.ROTATION_TWEEN_POSITION_VALUE.SMOOTH) {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ROTATION_TWEEN));
            if (rotationTween.length) {
                rotationTween[0] = Tweens.radians2degrees(this.rotation);
            }
        } else {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ROTATION_TWEEN));
        }

        const degrees: Array<number> = rotationTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(BaseElementAttributes.ROTATION_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(BaseElementAttributes.ROTATION_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(BaseElementAttributes.ROTATION_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ROTATION_TWEEN_KEY_TIMES))
            : null;

        Tweens.rotationTween(this, { degrees, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the scale tween
     *
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runScaleTween(node: Element): boolean {
        if (
            !node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_X) &&
            !node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_Y) &&
            !node.hasAttribute(BaseElementAttributes.SCALE_TWEEN)
        ) {
            return false;
        }

        Tweens.removeTweens(this, "scale");

        let scaleTweenX: Array<number>;
        let scaleTweenY: Array<number>;
        let scaleTween: Array<PIXI.Point>;
        let tweenValues: Array<PIXI.Point> = [];

        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_X)) {
            scaleTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SCALE_TWEEN_X));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_Y)) {
            scaleTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SCALE_TWEEN_Y));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN)) {
            scaleTween = ShapeElement._getPointsFromPointsAttribute(
                node.getAttribute(BaseElementAttributes.SCALE_TWEEN),
            ) as Array<PIXI.Point>;
        }

        let scaleTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.SCALE_TWEEN_POSITION,
            null,
        );

        if (scaleTween && scaleTween.length) {
            if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTween.map((value: PIXI.Point) => {
                    return {
                        x: value.x + this.scale.x,
                        y: value.y + this.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTween;
                if (tweenValues.length) {
                    tweenValues[0].x = this.scale.x;
                    tweenValues[0].y = this.scale.y;
                }
            } else {
                tweenValues = scaleTween;
            }
        } else if (scaleTweenX && scaleTweenY) {
            if (scaleTweenX.length === scaleTweenY.length) {
                if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue + this.scale.x,
                            y: scaleTweenY[i] + this.scale.y,
                        } as PIXI.Point;
                    });
                } else if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue,
                            y: scaleTweenY[i],
                        } as PIXI.Point;
                    });
                    if (tweenValues.length) {
                        tweenValues[0].x = this.scale.x;
                        tweenValues[0].y = this.scale.y;
                    }
                } else {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue,
                            y: scaleTweenY[i],
                        } as PIXI.Point;
                    });
                }
            }
        } else if (scaleTweenX) {
            if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue + this.scale.x,
                        y: this.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: this.scale.y,
                    } as PIXI.Point;
                });
                if (tweenValues.length) {
                    tweenValues[0].x = this.scale.x;
                }
            } else {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: this.scale.y,
                    } as PIXI.Point;
                });
            }
        } else if (scaleTweenY) {
            if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.scale.x,
                        y: scaleYValue + this.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.scale.x,
                        y: scaleYValue,
                    } as PIXI.Point;
                });
                if (tweenValues.length) {
                    tweenValues[0].y = this.scale.y;
                }
            } else {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.scale.x,
                        y: scaleYValue,
                    } as PIXI.Point;
                });
            }
        } else {
            return false;
        }

        const tweenEase: string = Attributes.assignStringAttribute(node, BaseElementAttributes.SCALE_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.SCALE_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(BaseElementAttributes.SCALE_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.SCALE_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SCALE_TWEEN_KEY_TIMES))
            : null;

        Tweens.scaleTween(this, { tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    public runTween(node: Element): boolean {
        if (node.hasAttribute(TweensAttributes.TWEEN)) {
            let tweens: Array<string> = ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN));

            for (let i: number = 0; i < tweens.length; i++) {
                //return if there are no values to tween
                if (!node.hasAttribute(TweensAttributes.TWEEN_VALUES + i)) {
                    return false;
                }

                let tween: string = tweens[i];

                const tweenValues: Array<number> = this.formatTweenValues(
                    tween,
                    ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN_VALUES + i)),
                );
                const tweenDuration: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DURATION + i) || "120",
                );
                const tweenEase: Array<string> = ArrayUtils.stringArray(
                    node.getAttribute(TweensAttributes.TWEEN_EASE + i) || "linear",
                );
                const tweenDelay: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DELAY + i) || "0",
                );
                const tweenLoop: boolean = Attributes.assignBooleanAttribute(
                    node,
                    TweensAttributes.TWEEN_LOOP + i,
                    null,
                );

                Tweens.removeTweens(this, tween);

                if (tween == "scale") {
                    tween = "scale.x";
                    tweens[i] = "scale.y";
                    i--;
                }

                Tweens.customTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
            }
        }

        return false;
    }

    private formatTweenValues(tween: string, values: Array<string>): Array<number> {
        let tweenValues: Array<number> = [];

        if (tween === "scale") {
            tween = "scale.x";
        }
        let property: Array<string> = tween.split(".");
        let value: number;

        for (let i: number = 0; i < values.length; i++) {
            let current = values[i];
            switch (current[current.length - 1]) {
                case "c": //if we want current value
                    if (property.length == 1) {
                        value = this[property[0]];
                        if (property[0] === "rotation") {
                            value = this.radians2degrees(value);
                        }
                        tweenValues.push(value);
                    } else if (property.length == 2) {
                        tweenValues.push(this[property[0]][property[1]]);
                    }

                    break;
                case "+":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] + value;
                    }
                    tweenValues.push(value);
                    break;
                case "-":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] - value;
                    }
                    tweenValues.push(value);
                    break;
                default:
                    tweenValues.push(parseFloat(current));
            }
        }

        return tweenValues;
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public degrees2radians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public radians2degrees(radians: number): number {
        return radians * (180 / Math.PI);
    }

    /**
     * Converts string from "points attribute" into array of points. E.g. "(1,6);(5,2);2;(2,4)". Point can be specified as single value for both axex.
     * @method _getPointsFromPointsAttribute
     * @return Array<PIXI.Points>
     */
    protected static _getPointsFromPointsAttribute(pointsString: string): Array<{ x: number; y: number }> {
        if (!pointsString) {
            return [];
        }

        return pointsString
            .replace(/\s/g, "")
            .split(";")
            .map((valuesPair: string) =>
                /\(|\)/.test(valuesPair) ? valuesPair.slice(1, -1).split(",") : [valuesPair, valuesPair],
            )
            .map((valuesPair: Array<string>) => {
                return { x: parseFloat(valuesPair[0]), y: parseFloat(valuesPair[1]) };
            });
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @retutn void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param s
     */
    protected logError(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }

    clone(): ShapeElement {
        let element: ShapeElement = new ShapeElement();
        element.removeChildren();

        element._x = this._x;
        element._y = this._y;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.tint = this.tint;
        element.blendMode = this.blendMode;
        element.name = this.name;
        element._tweenEngine = this._tweenEngine;

        element._width = this._width;
        element._height = this._height;
        element._lineWidth = this._lineWidth;
        element._lineColor = this._lineColor;
        element._lineAlpha = this._lineAlpha;
        element._fillAlpha = this._fillAlpha;
        element._color = this._color;

        element.redraw();

        return element;
    }
}

export const ShapeElementAttributes = {
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    /*
     * Tween alpha of this display object with children.
     * For duration use AlphaTweenDuration (number - frames).
     * For ease use AlphaTwenEase (string - name of easeing function).
     * @property AlphaTween
     * @type Array<numbers> - consist of two values (start alpha; end alpha) or three values (start alpha, middle alpha, end alpha)
     * @default null
     */
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    ALPHA_TWEEN_KEY_TIMES: "AlphaTweenKeyTimes",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    ALPHA_TWEEN_POSITION: "AlphaTweenPosition",
    ALPHA_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    BLEND_MODE: "BlendMode",
    /*
     * Shape's fill color.
     * @property Color
     * @type hexadecimal
     * @default ff0000
     */
    COLOR: "Color",
    FILL_ALPHA: "FillAlpha",
    /*
     * Shape's height.
     * @property Height
     * @type number
     * @default null
     */
    HEIGHT: "Height",
    LINE_ALPHA: "LineAlpha",
    LINE_COLOR: "LineColor",
    LINE_WIDTH: "LineWidth",
    /*
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type string
     * @default null
     */
    MASK: "Mask",
    PIVOT_X: "PivotX",
    PIVOT_Y: "PivotY",

    SCALE_X: "ScaleX",
    SCALE_Y: "ScaleY",
    SCALE: "Scale",

    STYLE: "Style",
    /*
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /*
     * Shape's width.
     * @property Width
     * @type number
     * @default null
     */
    WIDTH: "Width",
    /*
     * The X (horizontal) position of the shape, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * The Y (vertical) position of the shape, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",

    POSITION_X: "PositionX",
    POSITION_Y: "PositionY",
};

interface IShapeElementAttributes {
    x?: number;
    y?: number;
    width?: number;
    height?: number;

    color?: number;
    lineColor?: number;
    lineWidth?: number;
    alpha?: number;
    /* and color and so on */
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ShapeElement.ts
 **/